<template>
	<TransitionRoot as="template" :show="open">
		<Dialog as="div" static class="fixed z-20 inset-0 overflow-hidden" @close="$emit('close')" :open="open">
			<div class="absolute inset-0 overflow-hidden">
				<TransitionChild as="template" enter="ease-in-out duration-500" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in-out duration-500" leave-from="opacity-100" leave-to="opacity-0">
					<DialogOverlay class="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
				</TransitionChild>

				<div class="fixed inset-y-0 right-0 pl-2 sm:pl-10 max-w-full flex">
					<TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700" enter-from="translate-x-full" enter-to="translate-x-0" leave="transform transition ease-in-out duration-500 sm:duration-700" leave-from="translate-x-0" leave-to="translate-x-full">						
						<div class="w-screen max-w-2xl">
							<div class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl">
								<div class="min-h-0 flex-1 flex flex-col py-6 overflow-y-scroll">
									<div class="px-4 sm:px-6">
										<div class="flex items-start justify-between">
											<DialogTitle class="text-lg font-medium text-gray-900">
												{{ __('home::orders.photos_from_social_media') }}
											</DialogTitle>
											<div class="ml-3 h-7 flex items-center">
												<button class="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none" @click="$emit('close')">
													<span class="sr-only">Close panel</span>
													<XIcon class="h-6 w-6" aria-hidden="true" />
												</button>
											</div>
										</div>
									</div>
									<div class="mt-6 relative flex-1 px-4 sm:px-6">

										<div v-if="loading">
											<svg class="mx-auto mt-12 h-12 w-12 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
												<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
												<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
											</svg>
										</div>

										<div v-else>
											
											<div class="flex justify-between items-center">

												<div>

													<button 
														type="button" 
														class="inline-block mr-4 hover:opacity-100 border-gray-400 pb-2"
														:class="{
															'opacity-80': connection !== 'facebook',
															'opacity-100 border-b-4': connection === 'facebook',
														}" 
														@click="connection = 'facebook'"
													>
														<img src="/media/icons/facebook.svg" class="h-8 w-8 inline-block" />
													</button>

													<button 
														type="button" 
														class="inline-block hover:opacity-100 border-gray-400 pb-2"
														:class="{
															'opacity-80': connection !== 'instagram',
															'opacity-100 border-b-4': connection === 'instagram',
														}" 
														@click="connection = 'instagram'"
													>
														<img src="/media/icons/instagram.svg" class="h-8 w-8 inline-block" />
													</button>

												</div>

												<button 
													type="button"
													class="text-indigo-500"
													v-if="isConnected"
													@click="disconnect()"
												>
													{{ __('home::integrations.sign_out') }}
												</button>

											</div>

											<div class="mt-8">

												<!-- Facebook -->

												<div v-if="connection === 'facebook'">

													<div v-if="facebook.connected === false">

														<p class="text-muted">{{ __('home::integrations.connect_to_facebook_message') }}</p>

														<button 
															type="button" 
															class="mt-4 w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white focus:outline-none bg-indigo-600 hover:bg-indigo-700"
															@click="fbAuth()"
														>
															{{ __('home::integrations.connect_to_facebook_cta') }}
														</button>

													</div>

													<div v-else>

														<ul class="divide-y divide-gray-200" v-if="! facebook.selectedAlbum">
															<li v-for="album in facebook.albums" :key="album.id">
																<button 
																	type="button" 
																	class="block w-full text-left px-4 py-4 text-lg text-gray-700 hover:bg-gray-200 cursor-pointer"
																	@click="fbLoadPhotosFromAlbum(album)"
																>
																	{{ album.name }}
																</button>
															</li>
														</ul>

														<div v-if="facebook.selectedAlbum">

															<h2 class="flex items-center text-lg">
																<button type="button" @click="facebook.selectedAlbum = null" class="inline-block mr-4">
																	<ChevronLeftIcon class="w-6 h-6 text-gray-500" />
																</button>
																{{ facebook.selectedAlbum.name }}
															</h2>

															<div class="mt-6 grid grid-cols-3 gap-4">

																<div
																	v-for="photo in facebook.photos"
																	:key="photo.id"
																	class="h-36 bg-gray-100 bg-contain bg-center bg-no-repeat relative"
																	:style="{ backgroundImage: 'url(' + photo.thumb + ')' }"
																	@click="select('facebook', photo)"
																>
																	<div v-if="selected['facebook'].contains('id', photo.id)">
																		<div class="absolute top-0 right-0 w-full h-full bg-white opacity-50"></div>
																		<div class="relative h-36 z-10 flex items-center justify-center">
																			<CheckIcon class="h-10 w-10 text-black" />
																		</div>
																	</div>
																</div>

															</div>

														</div>

													</div>

												</div>

												<!-- Instagram -->

												<div v-if="connection === 'instagram'">

													<div v-if="instagram.connected === false">

														<p class="text-muted">{{ __('home::integrations.connect_to_instagram_message') }}</p>

														<button 
															type="button" 
															class="mt-4 w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white focus:outline-none bg-indigo-600 hover:bg-indigo-700"
															@click="igAuth()"
														>
															{{ __('home::integrations.connect_to_instagram_cta') }}
														</button>

													</div>

													<div v-else>

														<div class="mt-6 grid grid-cols-3 gap-4">

															<div
																v-for="photo in instagram.photos"
																:key="photo.id"
																class="h-36 bg-gray-100 bg-contain bg-center bg-no-repeat relative"
																:style="{ backgroundImage: 'url(' + photo.original + ')' }"
																@click="select('instagram', photo)"
															>
																<div v-if="selected['instagram'].contains('id', photo.id)">
																	<div class="absolute top-0 right-0 w-full h-full bg-white opacity-50"></div>
																	<div class="relative h-36 z-10 flex items-center justify-center">
																		<CheckIcon class="h-10 w-10 text-black" />
																	</div>
																</div>
															</div>

														</div>

													</div>

												</div>

												<!-- End -->

											</div>

										</div>
										
									</div>
								</div>
								<div class="flex-shrink-0 px-4 py-4 flex justify-end">
									<button type="button" class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" @click="$emit('close')">
										{{ __('forms::actions.cancel') }}
									</button>
									<button 
										type="button" 
										class="ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white focus:outline-none" 
										:class="{ 
											'bg-indigo-300': selected[connection].count() == 0,
											'bg-indigo-600 hover:bg-indigo-700': selected[connection].count() > 0,
										}"
										:disabled="selected[connection].count() == 0"
										@click="addPhotos()"
									>
										{{ __('forms::actions.add_photos') }} 
										<span v-if="selected[connection].count() > 0" class="inline-block pl-1">({{ selected[connection].count() }})</span>
									</button>
								</div>
							</div>
						</div>
					</TransitionChild>
				</div>
			</div>
		</Dialog>
	</TransitionRoot>
</template>

<script>
	import axios from 'axios'
	import collect from 'collect.js'

	import { Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
	import { CheckIcon, XIcon, ChevronLeftIcon } from '@heroicons/vue/outline'

	export default {
		components: {
			Dialog,
			DialogOverlay,
			DialogTitle,
			TransitionChild,
			TransitionRoot,
			CheckIcon,
			ChevronLeftIcon,
			XIcon,
		},
		props: {
			open: {
				type: Boolean,
				default: false,
			},
			order: {
				type: Object,
			},
			product: {
				type: Object,
			},
		},
		data() {
			return {
				initiated: false,
				loading: true,
				selected: {
					facebook: collect([]),
					instagram: collect([]),
				},
				connection: 'facebook',
				facebook: {
					connected: false,
					albums: [],
					selectedAlbum: null,
					photos: [],
				},
				instagram: {
					connected: false,
					accessToken: null,
					userId: null,
					photos: [],
				},
			}
		},
		computed: {
			isConnected: function() {
				return this[this.connection].connected;
			},
		},
		watch: {
			open: function(newVal, oldVal) {
				if (! this.initiated && newVal === true) {
					this.initFacebookSdk().then(() => this.loading = false);
				}
			},
		},
		methods: {
			initFacebookSdk() {

				var facebookAppId = this.$page.props.__facebook_app_id;

			    return new Promise(resolve => {

			        window.fbAsyncInit = function () {
			            FB.init({
			                appId: facebookAppId,
			                cookie: true,
			                xfbml: true,
			                version: 'v11.0'
			            });

			            resolve();
			        };

			        // Load facebook sdk script
			        (function (d, s, id) {
			            var js, fjs = d.getElementsByTagName(s)[0];
			            if (d.getElementById(id)) { return; }
			            js = d.createElement(s); js.id = id;
			            js.src = "https://connect.facebook.net/en_US/sdk.js";
			            fjs.parentNode.insertBefore(js, fjs);
			        }(document, 'script', 'facebook-jssdk'));    
			    });
			},
			fbAuth() {
				var self = this;
				self.loading = true;
				FB.login(function(response) {
					if (response.authResponse) {
						FB.api('/me/albums', function(response) {
							self.facebook.connected = true;
							self.facebook.albums = response.data;
						});
					} 
					self.loading = false;
				}, {
					scope: 'user_photos',
				});
			},
			fbLoadPhotosFromAlbum(album) {
				var self = this;
				self.loading = true;
				FB.api(album.id + '/photos', 'GET', {
					'fields': 'images',
				}, function(response) {

					self.facebook.photos = collect(response.data)
						.map(function (item) {
							return {
								id: item.id,
								original: item.images[0].source,
								thumb: item.images[item.images.length - 1].source,
							};
						})
						.toArray();

					self.facebook.selectedAlbum = album;
					self.loading = false;
				});
			},
			fbDisconnect() {
				var self = this;
				self.loading = true;
				FB.logout(function (response) {
					self.facebook = {
						connected: false,
						albums: [],
						selectedAlbum: null,
						photos: [],
					};
					self.loading = false;
				});
			},
			igAuth() {
				var self = this;
				self.loading = true;

				var instagramAppId = this.$page.props.__instagram_app_id;
				var callbackUrl = this.$route('home::instagram.auth');
				var authUrl = 'https://api.instagram.com/oauth/authorize?client_id=' 
					+ instagramAppId 
					+ '&redirect_uri=' 
					+ callbackUrl 
					+ '&scope=user_profile,user_media&response_type=code';

			    var popup = window.open(callbackUrl, '');

			    popup.onload = function() {

			        if(window.location.hash.length == 0) {
			            popup.open(authUrl, '_self');
			        }
			        // An interval runs to get the access token from the pop-up
			        var interval = setInterval(function() {
			            try {
			                
			                if(popup.location.hash.length) {

			                	var params = new URLSearchParams(popup.location.search);
			                    
			                    clearInterval(interval);
			                    popup.close();

			                    axios.post(self.$route('home::instagram.token'), {
			                    		code: params.get('code'),
				                    })
			                    	.then((res) => {
			                    		self.instagram.accessToken = res.data.access_token;
			                    		self.instagram.userId = res.data.user_id;
			                    		self.instagram.connected = true;
			                    		self.igLoadPhotos();			                    		
			                    	});
			                }
			            }
			            catch(evt) {
			                //
			            }
			        }, 100);

				};

			},
			igLoadPhotos() {
				this.loading = true;

				axios
					.get('https://graph.instagram.com/me/media?fields=id,media_url,media_type,children{id,media_url}&access_token=' + this.instagram.accessToken)
	            	.then((res) => {

	            		var photos = collect([]);

	            		collect(res.data.data).each((item) => {
	            			if (item.media_type == 'IMAGE') {
								photos.push({
									id: item.id,
									original: item.media_url,
								});
							}
							else if (item.media_type == 'CAROUSEL_ALBUM') {
								collect(item.children.data).each((child) => {
									photos.push({
										id: child.id,
										original: child.media_url,
									});
								});
							}
						});

	            		this.instagram.photos = photos.toArray();
	            		this.loading = false;                    		
	            	});
			},	
			igDisconnect() {
				this.instagram = {
					connected: false,
					accessToken: null,
					userId: null,
					photos: [],
				};
			},
			disconnect() {
				if (this.connection == 'facebook') {
					this.fbDisconnect();
				}
				else if (this.connection == 'instagram') {
					this.igDisconnect();
				}
			},
			select(connection, photo) {

				var items = this.selected[connection];

				if (items.contains('id', photo.id)) {
					var items = items.reject(item => item.id == photo.id);
				}
				else {
					items.push({
						id: photo.id,
						original: photo.original,
					});
				}

				this.selected[connection] = items;
			},
			addPhotos() {

				if (this.selected[this.connection].count() == 0) {
					return;
				}

				this.loading = true;

				var request = {
					photos: this.selected[this.connection].toArray(),
				};

				if (this.product) {
					request.product_id = this.product.id;
				}

				axios
                    .post(this.$route('home::orders.photos.add-social', { uuid: this.order.uuid }), request)
                    .then((res) => {
                        this.$emit('append-photos', res.data);
                        this.selected[this.connection] = collect([]);
                        this.loading = false;
                        this.$emit('close');
                    });

			},
		},
	}
</script>